// What /subscribe says, computed from the plans endpoint and checkout's
// refusals (PRD 87 §4). Kept apart from the page so the arithmetic and the
// wording are tested on their own.
import {
  NETWORK_ERROR_CODE,
  ProPlan,
  SubscriptionApiError,
  SubscriptionPlan,
} from "../services/subscriptionService";
import { Subscription } from "../types/auth";
import { computeIsPro } from "../utils/proAccess";

/** The order plans are listed in. Checkout sells exactly these. */
export const PRO_PLANS: readonly ProPlan[] = ["monthly", "annual"];

export const isProPlan = (plan: string): plan is ProPlan => (PRO_PLANS as readonly string[]).includes(plan);

const PLAN_LABEL: Record<ProPlan, string> = { monthly: "Monthly", annual: "Annual" };

export const planLabel = (plan: ProPlan): string => PLAN_LABEL[plan];

const INTERVAL_SUFFIX: Record<string, string> = { month: "/mo", year: "/yr" };

export const intervalSuffix = (interval: string): string => INTERVAL_SUFFIX[interval] ?? "";

/** "$5", "$4.99", or null for an unpriced plan (unknown is not free). */
export const formatPlanPrice = (cents: number | null, currency: string): string | null => {
  if (cents === null) return null;
  const whole = cents % 100 === 0;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
};

/**
 * The annual plan's saving against twelve monthly payments, as a whole
 * percentage, or null when it cannot be computed honestly: a plan missing or
 * unpriced, a currency mismatch, intervals that are not month and year, or no
 * saving at all. Integer cents throughout.
 */
export const annualSavingsPercent = (plans: SubscriptionPlan[]): number | null => {
  const monthly = plans.find((p) => p.plan === "monthly");
  const annual = plans.find((p) => p.plan === "annual");
  if (!monthly || !annual) return null;
  if (monthly.amount_cents === null || annual.amount_cents === null) return null;
  if (monthly.amount_cents <= 0 || monthly.interval !== "month" || annual.interval !== "year") return null;
  if (monthly.currency.toLowerCase() !== annual.currency.toLowerCase()) return null;

  const yearOfMonthly = monthly.amount_cents * 12;
  const percent = Math.round(((yearOfMonthly - annual.amount_cents) * 100) / yearOfMonthly);
  return percent >= 1 ? percent : null;
};

/** Why a plan cannot be picked, or null when it can. */
export const unsellableReason = (plan: SubscriptionPlan): string | null => {
  if (plan.sellable) return null;
  return plan.amount_cents === null ? "Price not set yet" : "Not available right now";
};

/** The plan picked first: the ?plan= one, else annual, else the first that can be bought. */
export const defaultPlan = (plans: SubscriptionPlan[], requested: string | null): ProPlan | null => {
  const sellable = plans.filter((p) => p.sellable && isProPlan(p.plan)).map((p) => p.plan as ProPlan);
  const wanted = requested?.trim().toLowerCase();
  if (wanted && isProPlan(wanted) && sellable.includes(wanted)) return wanted;
  if (sellable.includes("annual")) return "annual";
  return sellable[0] ?? null;
};

/** A date the person reads, from an ISO 8601 instant, or null when unreadable. */
export const formatLongDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

/**
 * The purchase has reached the person's row. Stricter than computeIsPro on
 * its own: someone buying during a comp or the open_signup window already
 * passes computeIsPro by `grandfathered_until`, before the webhook lands. The
 * row's own status turns active (Stripe `active` or `trialing`) only when the
 * Stripe subscription exists, and checkout refuses anyone whose row was
 * already active or retrying, so seeing it is seeing this purchase.
 */
export const purchaseLanded = (subscription: Subscription | null | undefined): subscription is Subscription =>
  !!subscription &&
  computeIsPro(subscription) &&
  (subscription.status === "active" || subscription.status === "grace_period");

/** The codes checkout and the plans endpoint refuse a purchase with (all 409s). */
export const REFUSAL_CODES = [
  "subscription_exists",
  "billed_by_revenuecat",
  "pro_included_with_business",
  "payment_not_required",
  "pro_already_covered",
] as const;

export const isRefusalError = (error: SubscriptionApiError): boolean =>
  error.status === 409 || (REFUSAL_CODES as readonly string[]).includes(error.code);

/** Stripe statuses that mean the last payment failed. */
const PAYMENT_FAILED_STATUSES = ["past_due", "unpaid", "incomplete"];

export interface RefusalCopy {
  title: string;
  body: string;
  /** subscription_exists is fixed in the billing portal; the rest have nothing to do. */
  offersPortal: boolean;
}

export const refusalCopy = (code: string, message: string, details: Record<string, unknown>): RefusalCopy => {
  switch (code) {
    case "subscription_exists": {
      const status = typeof details.subscription_status === "string" ? details.subscription_status : null;
      return {
        title: "You already have a subscription",
        body: status && PAYMENT_FAILED_STATUSES.includes(status)
          ? "Your last payment didn't go through. Update your card in the billing portal to keep Greenback Pro, rather than starting another subscription."
          : "Change your card, see receipts or cancel in the billing portal, rather than starting another subscription.",
        offersPortal: true,
      };
    }
    case "billed_by_revenuecat":
      return {
        title: "Your subscription is already active",
        body: "Your Greenback Pro is billed through our previous payment provider, and we're moving it over for you, card and all. There's nothing to buy.",
        offersPortal: false,
      };
    case "pro_included_with_business":
      return {
        title: "Pro is included with Greenback Business",
        body: "Your Greenback Business subscription includes Greenback Pro, so there's nothing to buy.",
        offersPortal: false,
      };
    case "payment_not_required":
      return {
        title: "Greenback Pro is free for you",
        body: "Your account doesn't need a subscription, so there's nothing to buy.",
        offersPortal: false,
      };
    case "pro_already_covered": {
      const until = formatLongDate(typeof details.covered_until === "string" ? details.covered_until : null);
      return {
        title: "You already have Greenback Pro",
        body: until
          ? `You have Greenback Pro until ${until}, so there's nothing to buy yet.`
          : "You already have Greenback Pro, so there's nothing to buy yet.",
        offersPortal: false,
      };
    }
    default:
      return {
        title: "Greenback Pro can't be bought right now",
        body: message || "Please try again later, or contact support if this keeps happening.",
        offersPortal: false,
      };
  }
};

const GENERIC_CHECKOUT_ERROR = "Something went wrong starting checkout. Please try again.";

/** What to say when checkout itself fails (the refusals above aside). */
export const checkoutErrorMessage = (error: SubscriptionApiError): string => {
  switch (error.code) {
    case "invalid_plan":
      return "That plan isn't one we offer. Choose another plan and try again.";
    case "not_sellable":
      return "That plan can't be bought right now. Choose another plan, or try again later.";
    case "billing_unavailable":
      return "Payments are unavailable right now. Please try again in a few minutes.";
    case "stripe_error":
      return "Our payment provider didn't respond. Please try again in a moment.";
    case NETWORK_ERROR_CODE:
      return "We couldn't reach Greenback. Check your connection and try again.";
    default:
      return GENERIC_CHECKOUT_ERROR;
  }
};

/** What to say when pricing cannot be loaded. */
export const plansErrorMessage = (error: SubscriptionApiError): string => {
  if (error.code === NETWORK_ERROR_CODE) return "We couldn't reach Greenback. Check your connection and try again.";
  if (error.code === "billing_unavailable") return "Payments are unavailable right now. Please try again in a few minutes.";
  return "We couldn't load pricing. Please try again.";
};

/** What to say when the billing portal cannot be opened. */
export const portalErrorMessage = (error: SubscriptionApiError): string => {
  switch (error.code) {
    case "no_billing_account":
      return "There's no billing account on file for you yet, so there's nothing to manage. If you think that's wrong, contact support.";
    case "billing_unavailable":
      return "Billing is unavailable right now. Please try again in a few minutes.";
    case "stripe_error":
      return "Our payment provider didn't respond. Please try again in a moment.";
    case NETWORK_ERROR_CODE:
      return "We couldn't reach Greenback. Check your connection and try again.";
    default:
      return "We couldn't open the billing portal. Please try again.";
  }
};

/** Any thrown value as a SubscriptionApiError, so callers branch on one shape. */
export const asSubscriptionError = (error: unknown): SubscriptionApiError =>
  error instanceof SubscriptionApiError
    ? error
    : new SubscriptionApiError(0, "unknown_error", error instanceof Error ? error.message : "Unknown error");
